/**
 * CommitmentStaleMarker — P4 of the Decision Propagation & Lineage PRD.
 *
 * PRD: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md §6.
 * Judgment: `judgeStaleness` in src/lib/commitmentViewCore.ts.
 *
 * Wherever a commitment is quoted — a plan narrative, a cycle surface, an
 * export, an API read — the render carries the version it is quoting and says
 * so when that version is no longer the authorized one. The failure this
 * prevents is the one the whole PRD is named after: a receiving surface
 * silently holding X′ while the record holds X².
 *
 * The one design rule that matters: a current version renders QUIETLY and drift
 * renders LOUDLY. A marker that shouts on every commitment is a marker people
 * stop reading, and then a stale quote passes unnoticed for exactly the reason
 * the marker existed.
 */

import { AlertTriangle } from 'lucide-react';
import { judgeStaleness, type StalenessReading } from '../../lib/commitmentViewCore';

export interface CommitmentStaleMarkerProps {
  /** The version this surface is showing. */
  quotedVersionNumber: number | null | undefined;
  /** The version number of the cycle's active authorization. */
  authoritativeVersionNumber: number | null | undefined;
  /** Pass a pre-computed reading instead of the two numbers. */
  reading?: StalenessReading;
  /** Hide the marker entirely when the quote is current. */
  hideWhenCurrent?: boolean;
  className?: string;
}

export function CommitmentStaleMarker({
  quotedVersionNumber,
  authoritativeVersionNumber,
  reading,
  hideWhenCurrent = false,
  className = '',
}: CommitmentStaleMarkerProps) {
  const verdict =
    reading ?? judgeStaleness({ quotedVersionNumber, authoritativeVersionNumber });

  if (!verdict.isDrift && hideWhenCurrent) return null;

  const tone = verdict.isDrift
    ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]'
    : 'border-[var(--rule)] bg-[var(--hover-tint)] text-[var(--text-ter)]';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${tone} ${className}`}
      title={verdict.detail}
      data-testid="commitment-stale-marker"
      data-verdict={verdict.verdict}
    >
      {verdict.isDrift && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span>{verdict.label}</span>
      {/*
        The tooltip is the affordance for a mouse. Screen readers and anyone
        reading a printed export get the full sentence, because drift a reader
        cannot perceive is drift the marker did not prevent.
      */}
      <span className="sr-only">{verdict.detail}</span>
    </span>
  );
}

export default CommitmentStaleMarker;
